import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getAllBuyerCategories,
  createBuyerCategory,
  deleteBuyerCategory,
  updateBuyerCategory,
} from "../redux/slices/buyerCategoriesSlice";
import {
  getAllSellerCategories,
  createSellerCategory,
  deleteSellerCategory,
  updateSellerCategory,
} from "../redux/slices/sellerCategoriesSlice";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CategoryManagement = () => {
  const dispatch = useDispatch();

  const { categories: buyerCategories } = useSelector(
    (state) => state.buyerCategories
  );

  const { categories: sellerCategories } = useSelector(
    (state) => state.sellerCategory
  );

  useEffect(() => {
    dispatch(getAllBuyerCategories());
    dispatch(getAllSellerCategories());
  }, [dispatch]);

  const [tab, setTab] = useState(0);
  const handleTabChange = (e, v) => setTab(v);
  const [buyerDialogOpen, setBuyerDialogOpen] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [discount, setDiscount] = useState("");
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [sellerDialogOpen, setSellerDialogOpen] = useState(false);
  const [sellerName, setSellerName] = useState("");
  const [gst, setGst] = useState("");
  const [sellerImage, setSellerImage] = useState("");
  const [sellerPreview, setSellerPreview] = useState("");
  const [editingSeller, setEditingSeller] = useState(null);

  const handleSellerImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setSellerImage(reader.result);
      setSellerPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const openBuyerModal = (cat = null) => {
    if (cat) {
      setEditingBuyer(cat);
      setBuyerName(cat.name);
      setDiscount(cat.discount);
    } else {
      setEditingBuyer(null);
      setBuyerName("");
      setDiscount("");
    }
    setBuyerDialogOpen(true);
  };

  const saveBuyerCategory = async () => {
    if (!buyerName || discount === "") {
      toast.error("Please fill all fields");
      return;
    }
    const data = { name: buyerName, discount: Number(discount) };
    try {
      if (editingBuyer) {
        await dispatch(
          updateBuyerCategory({ id: editingBuyer._id, categoryData: data })
        ).unwrap();
        toast.success("Buyer Category Updated");
      } else {
        await dispatch(createBuyerCategory(data)).unwrap();
        toast.success("Buyer Category Added");
      }
      dispatch(getAllBuyerCategories());
      setBuyerDialogOpen(false);
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const deleteBuyer = async (id) => {
    if (!window.confirm("Delete this buyer category?")) return;
    await dispatch(deleteBuyerCategory(id)).unwrap();
    dispatch(getAllBuyerCategories());
    toast.success("Buyer Category Deleted");
  };

  const openSellerModal = (cat = null) => {
    if (cat) {
      setEditingSeller(cat);
      setSellerName(cat.name);
      setGst(cat.gst);
      setSellerImage(cat.image);
      setSellerPreview(cat.image);
    } else {
      setEditingSeller(null);
      setSellerName("");
      setGst("");
      setSellerImage("");
      setSellerPreview("");
    }
    setSellerDialogOpen(true);
  };

  const saveSellerCategory = async () => {
    if (!sellerName || gst === "") return;

    const data = {
      name: sellerName,
      gst: Number(gst),
      image: sellerImage,
    };

    try {
      if (editingSeller) {
        await dispatch(
          updateSellerCategory({ id: editingSeller._id, categoryData: data })
        ).unwrap();
        toast.success("Product Category Updated");
      } else {
        await dispatch(createSellerCategory(data)).unwrap();
        toast.success("Product Category Added");
      }
      dispatch(getAllSellerCategories());
      setSellerDialogOpen(false);
    } catch (e) {
      toast.error("Error");
    }
  };

  const deleteSeller = async (id) => {
    if (!window.confirm("Delete product category?")) return;
    await dispatch(deleteSellerCategory(id)).unwrap();
    dispatch(getAllSellerCategories());
    toast.success("Product Category Deleted");
  };

  const [pageBuyer, setPageBuyer] = useState(0);
  const [rowsBuyer, setRowsBuyer] = useState(5);
  const [pageSeller, setPageSeller] = useState(0);
  const [rowsSeller, setRowsSeller] = useState(5);

  return (
    <Box sx={{ p: 3 }}>
      <h2>Category Management</h2>
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label="Buyer Category" {...a11yProps(0)} />
        <Tab label="Product Category" {...a11yProps(1)} />
      </Tabs>
      <hr />
      {tab === 0 && (
        <>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => openBuyerModal()}>
            Add Buyer Category
          </Button>
          <Paper sx={{ mt: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                   <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Discount (%)</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {buyerCategories
                    ?.slice(pageBuyer * rowsBuyer, pageBuyer * rowsBuyer + rowsBuyer)
                    .map((cat, index) => (
                      <TableRow key={cat._id}>
                         <TableCell>{index+1}</TableCell>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell>{cat.discount}%</TableCell>
                        <TableCell>
                          <Button sx={{ color: "#0b4064", "&:hover": { color: "#09324d" } }} onClick={() => openBuyerModal(cat)}>Edit</Button>
                          <Button color="error" onClick={() => deleteBuyer(cat._id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={buyerCategories?.length || 0}
              page={pageBuyer}
              onPageChange={(e, n) => setPageBuyer(n)}
              rowsPerPage={rowsBuyer}
              onRowsPerPageChange={(e) => {
                setRowsBuyer(parseInt(e.target.value, 10));
                setPageBuyer(0);
              }}
            />
          </Paper>
        </>
      )}

      {/* ---------------- Seller Category ---------------- */}
      {tab === 1 && (
        <>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => openSellerModal()}>
            Add Product Category
          </Button>

          <Paper sx={{ mt: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>GST (%)</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sellerCategories
                    ?.slice(pageSeller * rowsSeller, pageSeller * rowsSeller + rowsSeller)
                    .map((cat, index) => (
                      <TableRow key={cat._id}>
                        <TableCell>{index+1}</TableCell>
                        <TableCell>
                          <Avatar src={cat.image} sx={{ width: 50, height: 50 }} />
                        </TableCell>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell>{cat.gst}%</TableCell>
                        <TableCell>
                          <Button onClick={() => openSellerModal(cat)}>Edit</Button>
                          <Button color="error" onClick={() => deleteSeller(cat._id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sellerCategories?.length || 0}
              page={pageSeller}
              onPageChange={(e, n) => setPageSeller(n)}
              rowsPerPage={rowsSeller}
              onRowsPerPageChange={(e) => {
                setRowsSeller(parseInt(e.target.value, 10));
                setPageSeller(0);
              }}
            />
          </Paper>
        </>
      )}

      {/* Buyer Dialog */}
      <Dialog open={buyerDialogOpen} onClose={() => setBuyerDialogOpen(false)} fullWidth>
        <DialogTitle>{editingBuyer ? "Edit Buyer Category" : "Add Buyer Category"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Buyer Category Name"
            sx={{ mt: 2 }}
            value={buyerName}
            onChange={(e) => setBuyerName(e.target.value)}
          />

          <TextField
            fullWidth
            type="number"
            label="Discount (%)"
            sx={{ mt: 2 }}
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setBuyerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBuyerCategory}>
            {editingBuyer ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sellerDialogOpen} onClose={() => setSellerDialogOpen(false)} fullWidth>
        <DialogTitle>
          {editingSeller ? "Edit Product Category" : "Add Product Category"}
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="Product Category Name"
            sx={{ mt: 2 }}
            value={sellerName}
            onChange={(e) => setSellerName(e.target.value)}
          />

          <TextField
            fullWidth
            type="number"
            label="GST (%)"
            sx={{ mt: 2 }}
            value={gst}
            onChange={(e) => setGst(e.target.value)}
          />

          <input
            type="file"
            accept="image/*"
            style={{ marginTop: "20px" }}
            onChange={handleSellerImageChange}
          />

          {sellerPreview && (
            <Avatar
              src={sellerPreview}
              sx={{ width: 80, height: 80, mt: 2, borderRadius: 1 }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSellerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSellerCategory}>
            {editingSeller ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
